import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
const DIALOG_KEYS = {
    agency: 'agency',
    setAsideType: 'dsetaside',
    naicsCode: 'naics',
    postedAfter: 'posted',
    deadlineBefore: 'due'
};
/** Params that change the result set; changing any of them returns to page 1. */ const RESULT_KEYS = [
    'q',
    'urgency',
    'setaside',
    'source',
    'saved',
    ...Object.values(DIALOG_KEYS)
];
function setOrDelete(p, key, value) {
    if (value) p.set(key, value);
    else p.delete(key);
}
/**
 * Market desk context lives in the URL so Back, refresh and shared links
 * restore the same search, filters, view, page and open Notice (SAD-467).
 * Writers that change the result set drop `page`; `replace` keeps per-keystroke
 * style updates out of the history stack.
 */ export function useMarketDeskParams() {
    const [params, setParams] = useSearchParams();
    const state = useMemo(()=>{
        const filters = {};
        for (const [field, key] of Object.entries(DIALOG_KEYS)){
            const v = params.get(key);
            if (v) filters[field] = v;
        }
        const urgency = params.get('urgency');
        const pageNum = Number.parseInt(params.get('page') ?? '', 10);
        return {
            search: params.get('q') ?? '',
            filters,
            quickFilters: {
                urgency: urgency === 'urgent' || urgency === 'soon' ? urgency : 'all',
                setAside: params.get('setaside'),
                source: params.get('source'),
                saved: params.get('saved') === '1'
            },
            viewMode: params.get('view') === 'grid' ? 'grid' : 'list',
            page: Number.isFinite(pageNum) && pageNum > 1 ? pageNum - 1 : 0,
            notice: params.get('notice')
        };
    }, [
        params
    ]);
    const update = useCallback((mutate, opts)=>{
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            const before = RESULT_KEYS.map((k)=>next.get(k)).join('\u0000');
            mutate(next);
            if (RESULT_KEYS.map((k)=>next.get(k)).join('\u0000') !== before) next.delete('page');
            return next;
        }, {
            replace: opts?.replace ?? true
        });
    }, [
        setParams
    ]);
    const setSearch = useCallback((q)=>update((p)=>setOrDelete(p, 'q', q), {
            replace: true
        }), [
        update
    ]);
    const setFilters = useCallback((f)=>update((p)=>{
            for (const [field, key] of Object.entries(DIALOG_KEYS)){
                setOrDelete(p, key, f[field]);
            }
        }), [
        update
    ]);
    const setQuickFilters = useCallback((q)=>update((p)=>{
            setOrDelete(p, 'urgency', q.urgency === 'all' ? null : q.urgency);
            setOrDelete(p, 'setaside', q.setAside);
            setOrDelete(p, 'source', q.source);
            setOrDelete(p, 'saved', q.saved ? '1' : null);
        }), [
        update
    ]);
    const setViewMode = useCallback((v)=>update((p)=>setOrDelete(p, 'view', v === 'grid' ? 'grid' : null)), [
        update
    ]);
    const setPage = useCallback((page)=>update((p)=>setOrDelete(p, 'page', page > 0 ? String(page + 1) : null)), [
        update
    ]);
    /** Opening pushes a history entry (Back closes it); closing replaces. */ const setNotice = useCallback((noticeId, opts)=>update((p)=>setOrDelete(p, 'notice', noticeId), {
            replace: opts?.replace ?? noticeId === null
        }), [
        update
    ]);
    const clearAll = useCallback(()=>update((p)=>{
            for (const k of RESULT_KEYS)if (k !== 'q') p.delete(k);
        }), [
        update
    ]);
    return {
        ...state,
        setSearch,
        setFilters,
        setQuickFilters,
        setViewMode,
        setPage,
        setNotice,
        clearAll
    };
}
